import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faMapMarkedAlt, faEdit, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

// Number of rows to show per DataTable page
const ITEMS_PER_PAGE = 25;

const WarehousesTable = ({
  warehouses = [],
  loading = false,
  keyField = 'id',
  onViewWarehouse,
  onEditWarehouse,
  onDeleteWarehouse,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();

  const formatCapacity = useCallback((value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return t('common.symbols.emDash');
    const num = Math.round(Number(value));
    return `${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} m²`;
  }, [t]);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: t('warehouses.table.columns.name'),
        sortable: true,
        sortAccessor: (w) => (w?.name ?? '').toLowerCase(),
        renderCell: (w) => (
          <div>
            <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {w?.name ?? t('common.symbols.emDash')}
            </div>
          </div>
        ),
        skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'total_capacity',
        label: t('warehouses.table.columns.total_capacity'),
        sortable: true,
        align: 'right',
        sortAccessor: (w) => parseFloat(w?.total_capacity || 0),
        renderCell: (w) => (
          <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {formatCapacity(w?.total_capacity)}
          </span>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'address',
        label: t('warehouses.table.columns.address'),
        sortable: false,
        renderCell: (w) => (
          <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {w?.address ?? t('common.symbols.emDash')}
          </div>
        ),
        skeleton: <div className="w-48 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'google_maps_url',
        label: t('warehouses.table.columns.google_maps_url'),
        sortable: false,
        align: 'center',
        renderCell: (w) => (
          w?.google_maps_url ? (
            <a
              href={w.google_maps_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center space-x-2 text-sm"
              title={t('warehouses.table.columns.google_maps_url')}
            >
              <FontAwesomeIcon icon={faMapMarkedAlt} />
              <span className="sr-only">{t('warehouses.table.aria.openMap')}</span>
            </a>
          ) : (
            <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {t('common.symbols.emDash')}
            </span>
          )
        ),
        skeleton: <div className="w-6 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'actions',
        label: t('warehouses.table.columns.actions'),
        align: 'center',
        justify: 'center',
        alignItems: 'center',
        sortable: false,
        renderCell: (w) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewWarehouse?.(w.id)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.08),
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.BUTTONS.PRIMARY.background,
                  0.12
                );
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.BUTTONS.PRIMARY.background,
                  0.08
                );
              }}
              title={t('warehouses.table.aria.viewWarehouse')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>

            <button
              onClick={() => onEditWarehouse?.(w.id)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.STATUS.WARNING,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.STATUS.WARNING,
                  0.2
                );
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.STATUS.WARNING,
                  0.1
                );
              }}
              title={t('warehouses.table.aria.editWarehouse')}
            >
              <FontAwesomeIcon icon={faEdit} className="text-sm" />
            </button>

            {checkDeleteAccess() && (
              <button
                onClick={() => onDeleteWarehouse?.(w.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.ERROR,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.1
                  );
                }}
                title={t('warehouses.table.aria.deleteWarehouse')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-8 h-8 bg-gray-300 rounded-lg" />
            ))}
          </div>
        ),
      },
    ],
    [onDeleteWarehouse, onEditWarehouse, onViewWarehouse, checkDeleteAccess, formatCapacity, t]
  );

  const getRowProps = useCallback(
    () => ({
      className: 'hover:bg-opacity-50 transition-colors',
      style: {
        backgroundColor: 'transparent',
      },
    }),
    []
  );

  const emptyState = useMemo(
    () => ({
      icon: faBuilding,
      title: t('warehouses.table.emptyState.title'),
      message: t('warehouses.table.emptyState.message'),
    }),
    [t]
  );

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(warehouses) ? warehouses : []}
      loading={loading}
      keyField={keyField}
      itemsPerPage={ITEMS_PER_PAGE}
      defaultSort={{ field: 'name', direction: 'asc' }}
      getRowProps={getRowProps}
      emptyState={emptyState}
    />
  );
};

export default WarehousesTable;
